import type { TradePolicyCatalogField } from "../../../types/tradePolicy";
import { policyCatalogFieldDisplayLabel } from "../../../utils/policyCatalogFieldLabels";
import {
  catalogFieldUsesPercentUnit,
  catalogPercentHumanDisplayScale,
} from "./policyRuleNumericValue";
import type { EditorRuleNode } from "./policyTreeUtils";

const TRADE_TYPE_PATH = "trade.trade_type";

const OP_LABELS: Record<string, string> = {
  eq: "Equals",
  ne: "Not equal",
  in: "One of…",
  gte: "Greater or equal",
  lte: "Less or equal",
  range: "Min / Max (range)",
};

export function formatOperatorLabel(op: string): string {
  return OP_LABELS[op] ?? op;
}

function numericOperatorMeaning(op: string): string {
  switch (op) {
    case "gte":
      return "Matches trades where this field is greater than or equal to the number you enter.";
    case "lte":
      return "Matches trades where this field is less than or equal to the number you enter.";
    case "eq":
      return "Matches only when this field equals your number exactly (check units in the catalog text).";
    case "ne":
      return "Matches when this field is not equal to your number.";
    case "range":
      return "Matches trades where this field is within your Min/Max bounds. Min and Max are optional; set either or both.";
    default:
      return "Enter a number appropriate for this field.";
  }
}

export function policyRuleValueHelperText(
  node: EditorRuleNode,
  field: TradePolicyCatalogField | undefined,
  isEnum: boolean,
  isInOp: boolean,
  enumPickerValues: string[],
  numericHintForPath: (path: string) => string | null,
): string | null {
  void numericHintForPath;
  if (!field) return null;

  if (isEnum && isInOp && enumPickerValues.length > 0) {
    return "Select one or more allowed tokens from the catalog.";
  }
  if (isEnum && isInOp) {
    return "Must match catalog enum tokens (case-insensitive). Allowed values come from the server filter catalog for this field.";
  }
  if (isEnum && enumPickerValues.length > 0) {
    return null;
  }
  if (isEnum) {
    return "Type the exact catalog value; matching is case-insensitive. Use a number field (e.g. structure.adx) for raw thresholds.";
  }
  if (field.value_kind === "number") {
    const opHint = numericOperatorMeaning(node.op);
    if (catalogPercentHumanDisplayScale(field) > 1) {
      return `${opHint} Enter whole-number percent (e.g. 30 for 30% band width, 80 for 80%).`;
    }
    if (catalogFieldUsesPercentUnit(field)) {
      return `${opHint} Enter percent of price as shown in the catalog (e.g. 0.35 for 0.35% ATR).`;
    }
    return opHint;
  }
  return null;
}

/** Field dropdown options — display labels aligned with report/pick vocabulary. */
export function catalogFieldPickerOptions(
  catalogFieldsForPicker: TradePolicyCatalogField[],
  activePath: string,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): Array<{ value: string; label: string; keywords?: string }> {
  const toOption = (f: TradePolicyCatalogField) => {
    const display = policyCatalogFieldDisplayLabel(f.path, f.label);
    const catalogLabel = String(f.label ?? "").trim();
    return {
      value: f.path,
      label: display,
      keywords: [catalogLabel, f.path, f.value_kind].filter(Boolean).join(" "),
    };
  };
  const options = catalogFieldsForPicker.map(toOption);
  const path = activePath.trim();
  if (!path || options.some((o) => o.value === path)) return options;
  const field = fieldsByPath.get(path);
  if (!field) return options;
  return [toOption(field), ...options];
}
export function tradeTypeAxis(field: TradePolicyCatalogField): { long: string; short: string } | null {
  if (field.path !== TRADE_TYPE_PATH) return null;
  const set = new Set(field.enum_values ?? []);
  if (set.has("buy") && set.has("sell")) return { long: "buy", short: "sell" };
  if (set.has("long") && set.has("short")) return { long: "long", short: "short" };
  return null;
}

export function tradeTypeDirectionFromRow(
  row: EditorRuleNode,
  axis: { long: string; short: string },
): "long" | "short" | "both" | "unset" {
  const { long, short: shortTok } = axis;
  if (row.op === "in") {
    const s = new Set(row.valueEnumSet);
    if (s.has(long) && s.has(shortTok) && row.valueEnumSet.length >= 2) return "both";
    return "unset";
  }
  if (row.op === "eq") {
    if (row.valueEnum === long) return "long";
    if (row.valueEnum === shortTok) return "short";
    return "unset";
  }
  return "unset";
}

export function tradeDirectionSelectValue(
  node: EditorRuleNode,
  typeAxis: { long: string; short: string },
): string {
  const direction = tradeTypeDirectionFromRow(node, typeAxis);
  if (direction === "unset") return "";
  return direction;
}

export function applyTradeDirectionSelect(
  nodeId: string,
  value: string,
  typeAxis: { long: string; short: string },
  onUpdateRule: (id: string, patch: Partial<EditorRuleNode>) => void,
): void {
  if (!value) {
    onUpdateRule(nodeId, { op: "eq", valueEnum: "", valueEnumSet: [] });
    return;
  }
  const choice = value as "long" | "short" | "both";
  const { long, short: shortVal } = typeAxis;
  if (choice === "long") {
    onUpdateRule(nodeId, { op: "eq", valueEnum: long, valueEnumSet: [] });
  } else if (choice === "short") {
    onUpdateRule(nodeId, { op: "eq", valueEnum: shortVal, valueEnumSet: [] });
  } else {
    onUpdateRule(nodeId, { op: "in", valueEnum: "", valueEnumSet: [long, shortVal] });
  }
}

export function enumInSelectedTokens(row: EditorRuleNode): string[] {
  if (row.valueEnumSet.length > 0) return row.valueEnumSet;
  if (row.valueEnum.trim() === "") return [];
  return row.valueEnum
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
